import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import {
  ConcreteCalculatorInputs,
  ConcreteCalculatorResults,
  ConcreteType,
  OnAddToMaterialCallback,
} from '../../types/calculator.types';
import { Plus } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface ConcreteCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
}

const ConcreteCalculator: React.FC<ConcreteCalculatorProps> = ({
  onAddToMaterial,
}) => {
  const tCalc = useTranslation('calculators');

  // Sprint 30, story-151 review Critical #2. Previously a module-level hardcoded English
  // `SelectOption[]`, invisible to the guard's object-literal-only check.
  const concreteTypeOptions: SelectOption[] = [
    { value: ConcreteType.SLAB, label: tCalc.concrete.types.slab },
    { value: ConcreteType.SIDEWALK, label: tCalc.concrete.types.sidewalk },
    { value: ConcreteType.FOOTER, label: tCalc.concrete.types.footer },
    { value: ConcreteType.COLUMN, label: tCalc.concrete.types.column },
    { value: ConcreteType.TUBE, label: tCalc.concrete.types.tube },
    { value: ConcreteType.STAIRS, label: tCalc.concrete.types.stairs },
    { value: ConcreteType.CURB, label: tCalc.concrete.types.curb },
    { value: ConcreteType.GUTTER_BARRIER, label: tCalc.concrete.types.gutterBarrier },
  ];

  const columnShapeOptions: SelectOption[] = [
    { value: 'round', label: tCalc.concrete.shapes.round },
    { value: 'square', label: tCalc.concrete.shapes.square },
  ];
  const [inputs, setInputs] = useState<ConcreteCalculatorInputs>({
    calcType: ConcreteType.SLAB,
    length: '',
    width: '',
    thickness: '',
    height: '',
    diameter: '',
    side1: '',
    side2: '',
    numSteps: '',
    riserHeight: '',
    treadDepth: '',
    stepWidth: '',
    columnShape: 'round',
    curbDepth: '',
    gutterWidth: '',
    curbHeight: '',
    flagThickness: '',
    tubeWallThickness: '',
  });

  const [results, setResults] = useState<ConcreteCalculatorResults | null>(
    null,
  );
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they appear
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results]);

  const handleInputChange =
    (field: keyof ConcreteCalculatorInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputs((prev) => ({ ...prev, [field]: value }));
    };

  const clearInputs = () => {
    setInputs({
      calcType: inputs.calcType,
      length: '',
      width: '',
      thickness: '',
      height: '',
      diameter: '',
      side1: '',
      side2: '',
      numSteps: '',
      riserHeight: '',
      treadDepth: '',
      stepWidth: '',
      columnShape: 'round',
      curbDepth: '',
      gutterWidth: '',
      curbHeight: '',
      flagThickness: '',
      tubeWallThickness: '',
    });
    setResults(null);
  };

  const calculateConcrete = () => {
    setResults(null);
    let volumeCuFt = 0;

    const validateInputs = (inputsToValidate: string[]) => {
      for (const input of inputsToValidate) {
        if (isNaN(parseFloat(input)) || parseFloat(input) <= 0) {
          return false;
        }
      }
      return true;
    };

    try {
      switch (inputs.calcType) {
        case ConcreteType.SLAB:
        case ConcreteType.SIDEWALK:
        case ConcreteType.FOOTER:
          if (!validateInputs([inputs.length, inputs.width, inputs.thickness]))
            return;
          volumeCuFt =
            parseFloat(inputs.length) *
            parseFloat(inputs.width) *
            (parseFloat(inputs.thickness) / 12);
          break;

        case ConcreteType.STAIRS:
          if (
            !validateInputs([
              inputs.numSteps,
              inputs.riserHeight,
              inputs.treadDepth,
              inputs.stepWidth,
            ])
          )
            return;
          const totalRiseFt =
            (parseFloat(inputs.numSteps) * parseFloat(inputs.riserHeight)) / 12;
          const totalRunFt =
            (parseFloat(inputs.numSteps) * parseFloat(inputs.treadDepth)) / 12;
          volumeCuFt =
            0.5 * totalRiseFt * totalRunFt * parseFloat(inputs.stepWidth);
          volumeCuFt *= 1.1;
          break;

        case ConcreteType.COLUMN:
          if (inputs.columnShape === 'round') {
            if (!validateInputs([inputs.diameter, inputs.height])) return;
            const radiusFt = parseFloat(inputs.diameter) / 24;
            volumeCuFt =
              Math.PI * radiusFt * radiusFt * parseFloat(inputs.height);
          } else {
            if (!validateInputs([inputs.side1, inputs.side2, inputs.height]))
              return;
            const side1Ft = parseFloat(inputs.side1) / 12;
            const side2Ft = parseFloat(inputs.side2) / 12;
            volumeCuFt = side1Ft * side2Ft * parseFloat(inputs.height);
          }
          break;

        case ConcreteType.TUBE:
          if (
            !validateInputs([
              inputs.diameter,
              inputs.height,
              inputs.tubeWallThickness,
            ])
          )
            return;
          const outerRadiusFt = parseFloat(inputs.diameter) / 24;
          const innerDiameter =
            parseFloat(inputs.diameter) -
            2 * parseFloat(inputs.tubeWallThickness);
          if (innerDiameter <= 0) {
            return;
          }
          const innerRadiusFt = innerDiameter / 24;
          volumeCuFt =
            Math.PI *
            (Math.pow(outerRadiusFt, 2) - Math.pow(innerRadiusFt, 2)) *
            parseFloat(inputs.height);
          break;

        case ConcreteType.CURB:
          if (!validateInputs([inputs.length, inputs.curbDepth, inputs.curbHeight]))
            return;
          const curbVolume =
            parseFloat(inputs.length) *
            (parseFloat(inputs.curbDepth) / 12) *
            (parseFloat(inputs.curbHeight) / 12);
          volumeCuFt = curbVolume;
          break;

        case ConcreteType.GUTTER_BARRIER:
          if (
            !validateInputs([
              inputs.length,
              inputs.curbDepth,
              inputs.curbHeight,
              inputs.gutterWidth,
              inputs.flagThickness,
            ])
          )
            return;
          const gutterVolume =
            parseFloat(inputs.length) *
            (parseFloat(inputs.gutterWidth) / 12) *
            (parseFloat(inputs.flagThickness) / 12);
          const totalVolume =
            parseFloat(inputs.length) *
            (parseFloat(inputs.curbDepth) / 12) *
            (parseFloat(inputs.curbHeight) / 12);
          volumeCuFt = gutterVolume + totalVolume;
          break;

        default:
          return;
      }

      const volumeCuYards = volumeCuFt / 27;
      setResults({ result: volumeCuYards.toFixed(2) });
   
    } catch (error) {
      console.error('Calculation failed:', error);
    
    }
  };

  const renderInputs = () => {
    switch (inputs.calcType) {
      case ConcreteType.SLAB:
      case ConcreteType.SIDEWALK:
      case ConcreteType.FOOTER:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label label={tCalc.dimensions.lengthFt} htmlFor="length" />
              <Input
                id="length"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 20`}
                value={inputs.length}
                onChange={handleInputChange('length')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.dimensions.widthFt} htmlFor="width" />
              <Input
                id="width"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 10`}
                value={inputs.width}
                onChange={handleInputChange('width')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.dimensions.thicknessIn} htmlFor="thickness" />
              <Input
                id="thickness"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 4`}
                value={inputs.thickness}
                onChange={handleInputChange('thickness')}
                className="mt-1"
              />
            </div>
          </div>
        );

      case ConcreteType.STAIRS:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label label={tCalc.counts.steps} htmlFor="numSteps" />
              <Input
                id="numSteps"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 8`}
                value={inputs.numSteps}
                onChange={handleInputChange('numSteps')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.riserHeightIn} htmlFor="riserHeight" />
              <Input
                id="riserHeight"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 7`}
                value={inputs.riserHeight}
                onChange={handleInputChange('riserHeight')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.treadDepthIn} htmlFor="treadDepth" />
              <Input
                id="treadDepth"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 11`}
                value={inputs.treadDepth}
                onChange={handleInputChange('treadDepth')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.stepWidthFt} htmlFor="stepWidth" />
              <Input
                id="stepWidth"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 3`}
                value={inputs.stepWidth}
                onChange={handleInputChange('stepWidth')}
                className="mt-1"
              />
            </div>
          </div>
        );

      case ConcreteType.COLUMN:
        return (
          <div className="space-y-4">
            <div>
              <Label label={tCalc.concrete.columnShape} htmlFor="columnShape" />
              <div className="mt-1">
                <ReactSelect
                  options={columnShapeOptions}
                  value={columnShapeOptions.find(
                    (opt) => opt.value === inputs.columnShape,
                  )}
                  onChange={(option) => {
                    if (option && 'value' in option) {
                      setInputs((prev) => ({
                        ...prev,
                        columnShape: option.value as 'round' | 'square',
                      }));
                    }
                  }}
                  isClearable={false}
                  isSearchable={false}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {inputs.columnShape === 'round' ? (
                <div>
                  <Label label={tCalc.dimensions.diameterIn} htmlFor="diameter" />
                  <Input
                    id="diameter"
                    type="number"
                    placeholder={`${tCalc.actions.examplePrefix}, 12`}
                    value={inputs.diameter}
                    onChange={handleInputChange('diameter')}
                    className="mt-1"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <Label label={tCalc.concrete.side1In} htmlFor="side1" />
                    <Input
                      id="side1"
                      type="number"
                      placeholder={`${tCalc.actions.examplePrefix}, 10`}
                      value={inputs.side1}
                      onChange={handleInputChange('side1')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label label={tCalc.concrete.side2In} htmlFor="side2" />
                    <Input
                      id="side2"
                      type="number"
                      placeholder={`${tCalc.actions.examplePrefix}, 10`}
                      value={inputs.side2}
                      onChange={handleInputChange('side2')}
                      className="mt-1"
                    />
                  </div>
                </>
              )}
              <div>
                <Label label={tCalc.dimensions.heightFt} htmlFor="height" />
                <Input
                  id="height"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 8`}
                  value={inputs.height}
                  onChange={handleInputChange('height')}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        );

      case ConcreteType.TUBE:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label label={tCalc.dimensions.diameterIn} htmlFor="diameter" />
              <Input
                id="diameter"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 12`}
                value={inputs.diameter}
                onChange={handleInputChange('diameter')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.wallThicknessIn} htmlFor="tubeWallThickness" />
              <Input
                id="tubeWallThickness"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 1`}
                value={inputs.tubeWallThickness}
                onChange={handleInputChange('tubeWallThickness')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.dimensions.heightFt} htmlFor="height" />
              <Input
                id="height"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 8`}
                value={inputs.height}
                onChange={handleInputChange('height')}
                className="mt-1"
              />
            </div>
          </div>
        );

      case ConcreteType.CURB:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label label={tCalc.dimensions.lengthFt} htmlFor="length" />
              <Input
                id="length"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 50`}
                value={inputs.length}
                onChange={handleInputChange('length')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.curbDepthIn} htmlFor="curbDepth" />
              <Input
                id="curbDepth"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 6`}
                value={inputs.curbDepth}
                onChange={handleInputChange('curbDepth')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.curbHeightIn} htmlFor="curbHeight" />
              <Input
                id="curbHeight"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 12`}
                value={inputs.curbHeight}
                onChange={handleInputChange('curbHeight')}
                className="mt-1"
              />
            </div>
          </div>
        );

      case ConcreteType.GUTTER_BARRIER:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label label={tCalc.dimensions.lengthFt} htmlFor="length" />
              <Input
                id="length"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 50`}
                value={inputs.length}
                onChange={handleInputChange('length')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.curbDepthIn} htmlFor="curbDepth" />
              <Input
                id="curbDepth"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 6`}
                value={inputs.curbDepth}
                onChange={handleInputChange('curbDepth')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.curbHeightIn} htmlFor="curbHeight" />
              <Input
                id="curbHeight"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 12`}
                value={inputs.curbHeight}
                onChange={handleInputChange('curbHeight')}
                className="mt-1"
              />
            </div>
            <div>
              <Label label={tCalc.concrete.gutterWidthIn} htmlFor="gutterWidth" />
              <Input
                id="gutterWidth"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 18`}
                value={inputs.gutterWidth}
                onChange={handleInputChange('gutterWidth')}
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label label={tCalc.concrete.flagThicknessIn} htmlFor="flagThickness" />
              <Input
                id="flagThickness"
                type="number"
                placeholder={`${tCalc.actions.examplePrefix}, 6`}
                value={inputs.flagThickness}
                onChange={handleInputChange('flagThickness')}
                className="mt-1"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Sprint 30 change request (story-151 QA re-review, Bug #1). Was a plain `switch` returning
  // hardcoded English literals — a switch-return value, invisible to the guard's
  // object-literal-only check. Follows `web/src/app/utils/taskDisplay.ts`'s house pattern:
  // exhaustive switch, each arm returning a catalog key, `default` returning the catalog's own
  // "unknown" string.
  const getCalculationTypeLabel = () => {
    switch (inputs.calcType) {
      case ConcreteType.SLAB:
        return tCalc.concrete.dimensionsHeadingSlab;
      case ConcreteType.SIDEWALK:
        return tCalc.concrete.dimensionsHeadingSidewalk;
      case ConcreteType.FOOTER:
        return tCalc.concrete.dimensionsHeadingFooter;
      case ConcreteType.STAIRS:
        return tCalc.concrete.dimensionsHeadingStairs;
      case ConcreteType.COLUMN:
        return tCalc.concrete.dimensionsHeadingColumn;
      case ConcreteType.TUBE:
        return tCalc.concrete.dimensionsHeadingTube;
      case ConcreteType.CURB:
        return tCalc.concrete.dimensionsHeadingCurb;
      case ConcreteType.GUTTER_BARRIER:
        return tCalc.concrete.dimensionsHeadingGutterBarrier;
      default:
        return tCalc.concrete.dimensionsHeadingDefault;
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.concrete}
      </h3>

      <div className="mb-6">
        <Label label={tCalc.concrete.selectWhatToCalculate} htmlFor="calcType" />
        <div className="mt-1">
          <ReactSelect
            options={concreteTypeOptions}
            value={concreteTypeOptions.find(
              (opt) => opt.value === inputs.calcType,
            )}
            onChange={(option) => {
              if (option && 'value' in option) {
                setInputs((prev) => ({
                  ...prev,
                  calcType: option.value as ConcreteType,
                }));
                clearInputs();
              }
            }}
            isClearable={false}
            isSearchable={false}
          />
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {getCalculationTypeLabel()}
        </h4>
        {renderInputs()}
      </div>

      <div className="flex gap-4 mb-6">
        <Button
          fullWidth
          variant="primary"
          size="lg"
          onClick={calculateConcrete}>
          {tCalc.actions.calculate}
        </Button>
        <Button fullWidth variant="tertiary" size="lg" onClick={clearInputs}>
          {tCalc.actions.clear}
        </Button>
      </div>

      {results && (
        <div
          ref={resultsRef}
          className="p-4 mb-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {tCalc.actions.result}
          </h4>
          <div className="flex items-center justify-between">
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{tCalc.concrete.concreteNeeded}</span>{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-medium text-lg">
                {results.result}
              </span>{' '}
              {tCalc.dimensions.cubicYards}
            </p>
            {onAddToMaterial && (
              <Button
                type="button"
                variant="primary"
                size="md"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() =>
                  onAddToMaterial({
                    amount: parseFloat(results.result),
                    amountType: 'cubicYards',
                    amountSystem: 'imperial',
                    suggestedName: tCalc.materials.concrete,
                    resultLabel: tCalc.concrete.concreteNeeded,
                  })
                }>
                {tCalc.actions.addToMaterial}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcreteCalculator;
